import {
  BoxGeometry,
  CircleGeometry,
  Color,
  CylinderGeometry,
  DirectionalLight,
  Group,
  HemisphereLight,
  Mesh,
  MeshBasicMaterial,
  NoToneMapping,
  PerspectiveCamera,
  Scene,
  SphereGeometry,
  SRGBColorSpace,
  TorusGeometry,
  Vector3,
  WebGLRenderer,
} from 'three';
import { GeoBuilder } from './geo';
import { M, segment } from './car/kit';
import { mix, PAL, type PaletteName } from './palette';
import { toonMaterial } from './toon';
import { LIGHT } from './world';

/**
 * Yesterday's podium as little figures: the game's big-headed drivers stood up
 * on their own two feet, in cream overalls and red scarves, each in their own
 * helmet. The winner holds up a cup (the poster hangs the laurels behind). Drawn once each,
 * flat-lit like the poster's car, into small transparent pictures the poster
 * lays out on its podium. Also the driving lesson's helmet.
 * Local frame: facing +x, left = -z, standing on y = 0.
 */
export interface FigureSpec {
  helmet: PaletteName;
  stripe: PaletteName;
  /** The top step: the cup held high. */
  winner: boolean;
}

/** Helmet colours and their stripe, handed out by name so a driver keeps theirs from day to day. */
const HELMETS: [PaletteName, PaletteName][] = [
  ['red', 'cream'],
  ['ochre', 'red'],
  ['cypress', 'cream'],
  ['terracotta', 'cream'],
  ['olive', 'cream'],
  ['cream', 'red'],
  ['brown', 'ochre'],
];

function nameHash(s: string): number {
  let h = 2166136261;
  for (const ch of s.toLowerCase()) h = Math.imul(h ^ (ch.codePointAt(0) ?? 0), 16777619);
  return h >>> 0;
}

/** Each driver's figure: their own helmet (never the same as another's on the same podium). */
export function podiumFigures(names: string[]): FigureSpec[] {
  const taken = new Set<number>();
  return names.map((name, i) => {
    let k = nameHash(name) % HELMETS.length;
    while (taken.has(k)) k = (k + 1) % HELMETS.length;
    taken.add(k);
    return { helmet: HELMETS[k][0], stripe: HELMETS[k][1], winner: i === 0 };
  });
}

/** Every figure there is: each helmet, standing and holding the cup (scripts/paper-figures.mjs draws them all). */
export function everyFigure(): FigureSpec[] {
  return HELMETS.flatMap(([helmet, stripe]) => [false, true].map((winner) => ({ helmet, stripe, winner })));
}

/** A figure's picture drawn beforehand (public/paper/figures, by scripts/paper-figures.mjs), for a browser without 3D. */
export function figureFile(spec: FigureSpec): string {
  return `paper/figures/${spec.helmet}-${spec.stripe}${spec.winner ? '-cup' : ''}.png`;
}

const rod = (g: GeoBuilder, a: Vector3, b: Vector3, r: number, color: Color): void => {
  const { len, m } = segment(a, b);
  g.add(new CylinderGeometry(r, r, len, 7), color, m);
};

/** The big round helmet, its stripe over the crown, the goggles and a chin. */
function addHead(g: GeoBuilder, at: Vector3, scale: number, helmet: Color, stripe: Color): void {
  const S = (x: number, y: number, z: number, rx = 0, ry = 0, rz = 0, sx = 1, sy = sx, sz = sx): ReturnType<typeof M> =>
    M(at.x + x * scale, at.y + y * scale, at.z + z * scale, rx, ry, rz, sx * scale, sy * scale, sz * scale);
  const lens = mix('cream', 'asphalt', 0.45);
  g.add(new SphereGeometry(0.36, 18, 14), helmet, S(0, 0.2, 0), true);
  // The stripe front to back over the crown.
  g.add(new SphereGeometry(0.366, 24, 2, Math.PI + 0.62, Math.PI - 0.4, Math.PI / 2 - 0.13, 0.26), stripe, S(0, 0.2, 0, Math.PI / 2, 0, 0), true);
  g.add(new SphereGeometry(0.2, 10, 8), mix('terracotta', 'cream', 0.3), S(0.17, 0.02, 0), true);
  for (const s of [-1, 1]) {
    g.add(new CylinderGeometry(0.13, 0.14, 0.12, 14), PAL.brown, S(0.3, 0.17, s * 0.145, 0, 0, Math.PI / 2));
    g.add(new CircleGeometry(0.105, 16), lens, S(0.365, 0.17, s * 0.145, 0, Math.PI / 2, 0));
  }
  // The goggles' strap round the back.
  g.add(new TorusGeometry(0.36, 0.03, 5, 22), PAL.brown, S(0, 0.17, 0, Math.PI / 2, 0, 0));
}

/** One standing driver, all in one vertex-coloured mesh. */
function figure(spec: FigureSpec): Group {
  const g = new GeoBuilder();
  const suit = PAL.cream;
  const dark = PAL.brown;
  const scarf = PAL.red;
  for (const s of [-1, 1]) {
    // Boots and stubby legs.
    g.add(new SphereGeometry(0.11, 10, 8), dark, M(0.05, 0.06, s * 0.11, 0, 0, 0, 1.4, 0.7, 0.95), true);
    g.add(new CylinderGeometry(0.085, 0.095, 0.26, 8), suit, M(0, 0.21, s * 0.105));
  }
  // A round barrel of a body, belted.
  g.add(new CylinderGeometry(0.21, 0.235, 0.34, 12), suit, M(0, 0.5, 0));
  g.add(new SphereGeometry(0.235, 12, 8), suit, M(0, 0.34, 0, 0, 0, 0, 1, 0.45, 1), true);
  g.add(new SphereGeometry(0.215, 12, 8), suit, M(0, 0.67, 0, 0, 0, 0, 1, 0.5, 1), true);
  g.add(new CylinderGeometry(0.24, 0.24, 0.05, 12), dark, M(0, 0.41, 0));
  // Arms: the winner's right one up with the cup, the other hand on the hip; the others both on the hips.
  const shoulder = (s: number): Vector3 => new Vector3(0, 0.66, s * 0.24);
  const hipArm = (s: number): void => {
    const elbow = new Vector3(-0.05, 0.53, s * 0.4);
    const hand = new Vector3(0.05, 0.43, s * 0.26);
    rod(g, shoulder(s), elbow, 0.065, suit);
    rod(g, elbow, hand, 0.06, suit);
    g.add(new SphereGeometry(0.075, 8, 6), dark, M(hand.x, hand.y, hand.z), true);
  };
  if (spec.winner) {
    // Held out wide and high, clear of the big helmet.
    const s = -1;
    const elbow = new Vector3(0.02, 0.8, s * 0.46);
    const hand = new Vector3(0.05, 0.99, s * 0.56);
    rod(g, shoulder(s), elbow, 0.065, suit);
    rod(g, elbow, hand, 0.06, suit);
    g.add(new SphereGeometry(0.075, 8, 6), dark, M(hand.x, hand.y, hand.z), true);
    // The cup: base, stem, bowl and two handles, in gold.
    const gold = PAL.ochre;
    const cx = hand.x;
    const cz = hand.z;
    g.add(new CylinderGeometry(0.075, 0.09, 0.045, 10), mix('ochre', 'brown', 0.25), M(cx, hand.y + 0.08, cz));
    g.add(new CylinderGeometry(0.025, 0.035, 0.09, 8), gold, M(cx, hand.y + 0.145, cz));
    g.add(new CylinderGeometry(0.115, 0.045, 0.15, 12), gold, M(cx, hand.y + 0.26, cz));
    for (const k of [-1, 1]) g.add(new TorusGeometry(0.05, 0.016, 5, 10), gold, M(cx, hand.y + 0.27, cz + k * 0.115, 0, 0, 0));
    hipArm(1);
  } else {
    hipArm(-1);
    hipArm(1);
  }
  // The red scarf: a knot at the throat and its tail blowing off to one side.
  g.add(new SphereGeometry(0.08, 8, 6), scarf, M(0.19, 0.75, 0.04), true);
  g.add(new BoxGeometry(0.04, 0.3, 0.11), scarf, M(0.14, 0.64, 0.2, 0.55, 0, 0.35));
  g.add(new BoxGeometry(0.04, 0.22, 0.1), scarf, M(0.06, 0.5, 0.3, 0.9, 0, 0.5));
  addHead(g, new Vector3(0, 0.76, 0), 1.1, PAL[spec.helmet], PAL[spec.stripe]);

  const group = new Group();
  group.add(new Mesh(g.build(), toonMaterial(0xffffff, { vertexColors: true })));
  const shadow = new Mesh(new CircleGeometry(1, 28), new MeshBasicMaterial({ color: PAL.brown, transparent: true, opacity: 0.22 }));
  shadow.rotation.x = -Math.PI / 2;
  shadow.scale.set(0.42, 0.3, 1);
  shadow.position.set(0.02, 0.003, 0);
  group.add(shadow);
  return group;
}

/** The lesson's helmet on its own: cream with a red stripe, like the instructor's. */
function helmetOnly(): Group {
  const g = new GeoBuilder();
  addHead(g, new Vector3(0, 0, 0), 1, PAL.cream, PAL.red);
  const group = new Group();
  group.add(new Mesh(g.build(), toonMaterial(0xffffff, { vertexColors: true })));
  return group;
}

interface Shot {
  key: string;
  build: () => Group;
  /** What to fit in the frame. */
  box: { min: Vector3; max: Vector3 };
  azimuth: number;
  elevation: number;
  w: number;
  h: number;
}

const cache = new Map<string, string>();

/** Draw pictures in one go on a throwaway canvas (its 3D context is let go straight after). */
function draw(shots: Shot[]): void {
  const todo = shots.filter((s) => !cache.has(s.key));
  if (!todo.length) return;
  const canvas = document.createElement('canvas');
  let renderer: WebGLRenderer;
  try {
    renderer = new WebGLRenderer({ canvas, antialias: true, alpha: true, premultipliedAlpha: true, preserveDrawingBuffer: true });
  } catch {
    for (const s of todo) cache.set(s.key, '');
    return;
  }
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.toneMapping = NoToneMapping;
  renderer.setClearColor(0x000000, 0);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  const scene = new Scene();
  scene.add(new HemisphereLight(new Color(LIGHT.skyColor), new Color(LIGHT.groundColor), 0.62 * Math.PI));
  const sun = new DirectionalLight(new Color(LIGHT.sunColor), 0.78 * Math.PI);
  sun.position.set(5, 7, -4);
  scene.add(sun);
  const cam = new PerspectiveCamera(20, 1, 0.1, 100);
  for (const s of todo) {
    renderer.setSize(s.w, s.h, false);
    cam.aspect = s.w / s.h;
    const az = (s.azimuth * Math.PI) / 180;
    const el = (s.elevation * Math.PI) / 180;
    const dir = new Vector3(Math.cos(el) * Math.cos(az), Math.sin(el), -Math.cos(el) * Math.sin(az));
    const centre = s.box.min.clone().add(s.box.max).multiplyScalar(0.5);
    const half = s.box.max.clone().sub(s.box.min).multiplyScalar(0.5);
    const tanV = Math.tan((cam.fov * Math.PI) / 360);
    const tanH = tanV * cam.aspect;
    const d = Math.max(Math.hypot(half.x, half.z) / tanH, half.y / tanV) * 1.04 + Math.hypot(half.x, half.z);
    cam.position.copy(centre).addScaledVector(dir, d);
    cam.lookAt(centre);
    cam.updateProjectionMatrix();
    const obj = s.build();
    scene.add(obj);
    renderer.render(scene, cam);
    cache.set(s.key, canvas.toDataURL('image/png'));
    scene.remove(obj);
    obj.traverse((o) => {
      const m = o as Mesh;
      m.geometry?.dispose();
      (m.material as { dispose?: () => void } | undefined)?.dispose?.();
    });
  }
  renderer.dispose();
  renderer.forceContextLoss();
}

/** The same frame for every figure, so they stand to the same scale on their steps (the cup included). */
const FIGURE_BOX = { min: new Vector3(-0.32, 0, -0.66), max: new Vector3(0.32, 1.44, 0.5) };

/** Each figure's picture (a data URL; '' where 3D drawing isn't possible). */
export function figurePictures(specs: FigureSpec[]): string[] {
  const shots = specs.map((spec) => ({
    key: `fig|${spec.helmet}|${spec.stripe}|${spec.winner ? 1 : 0}`,
    build: () => figure(spec),
    box: FIGURE_BOX,
    azimuth: 24,
    elevation: 7,
    w: 112,
    h: 150,
  }));
  draw(shots);
  return shots.map((s) => cache.get(s.key) ?? '');
}

/** The driving lesson's helmet (a data URL). */
export function helmetPicture(): string {
  const shot: Shot = {
    key: 'helmet',
    build: helmetOnly,
    box: { min: new Vector3(-0.3, -0.16, -0.34), max: new Vector3(0.38, 0.56, 0.34) },
    azimuth: 38,
    elevation: 10,
    w: 96,
    h: 96,
  };
  draw([shot]);
  return cache.get('helmet') ?? '';
}
